import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Ensure cookies are always sent (needed for Emergent Google session_token cookie)
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

const TOKEN_KEY = "fitkiwi_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (t) => {
    try {
      const headers = t ? { Authorization: `Bearer ${t}` } : undefined;
      const res = await axios.get(`${API}/auth/me`, { headers, withCredentials: true });
      setUser(res.data);
    } catch (_e) {
      if (t) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe(token);
  }, [token, fetchMe]);

  const persistJwtAuth = (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.user);
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    persistJwtAuth(res.data);
    return res.data.user;
  };

  const signup = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/signup`, { name, email, password });
    persistJwtAuth(res.data);
    return res.data.user;
  };

  const setGoogleUser = (u) => {
    // Cookie session already set by backend; just store user in memory
    setUser(u);
  };

  const refreshMe = async () => {
    await fetchMe(token);
  };

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_e) {
      // ignore
    }
    setUser(null);
  };

  const authHeaders = () => (token ? { Authorization: `Bearer ${token}` } : {});

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, signup, logout, authHeaders, setGoogleUser, refreshMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
